import PropTypes from 'prop-types';
import React from 'react';
import {
  FormControl,
  Stack,
  Box,
  RadioGroup,
  TextField,
  FormControlLabel,
  FormLabel,
  Radio,
  Typography,
  Button,
  Divider
} from '@mui/material';
import { Icon } from '@iconify/react';
import questionMarkCircleFill from '@iconify/icons-eva/question-mark-circle-fill';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import arrowCircleUpOutline from '@iconify/icons-eva/arrow-circle-up-outline';
import arrowCircleDownOutline from '@iconify/icons-eva/arrow-circle-down-outline';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import dayjs from 'dayjs';
import 'dayjs/locale/id';
import isEligible from '../utils/roleEligibleGetter';

import { fCurrency } from '../utils/formatNumber';

export default function QuestionsGenerator({
  item,
  errors,
  touched,
  values,
  isSubmitting,
  handleSubmit,
  getFieldProps,
  setSubmitting,
  setFieldValue,
  secondaryData,
  dataDukungs,
  setDataDukungs,
  addTextArrayItem,
  removeTextArrayItem,
  setTextArrayValue,
  changeTextArrayPosition,
  layananStatus,
  setDateValue
}) {
  const theme = useTheme();
  dayjs.locale('id');
  return (
    <>
      <>
        {['currency'].includes(item.type) && (
          <TextField
            type="text"
            required={Boolean(item.required)}
            fullWidth
            label={item.label}
            {...getFieldProps(item.id)}
            error={Boolean(touched[item.id] && errors[item.id])}
            helperText={touched[item.id] && errors[item.id]}
            value={fCurrency(values[item.id])}
            placeholder={item.placeholder}
          />
        )}
      </>
      <>
        {['textinput', 'textarea', 'password'].includes(item.type) && (
          <TextField
            type={item.type === 'password' ? 'password' : 'text'}
            multiline={item.type === 'textarea'}
            required={Boolean(item.required)}
            fullWidth
            autoComplete={item.id}
            label={item.label}
            {...getFieldProps(item.id)}
            error={Boolean(touched[item.id] && errors[item.id])}
            helperText={touched[item.id] && errors[item.id]}
            value={values[item.id]}
            placeholder={item.placeholder}
          />
        )}
      </>

      <>
        {['date'].includes(item.type) && (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="id">
            <DesktopDatePicker
              required
              error={Boolean(touched[item.id] && errors[item.id])}
              label={`${item.label} ${item.required && '*'}`}
              inputFormat="DD MMMM YYYY"
              value={values[item.id] || ''}
              renderInput={(params) => <TextField {...params} />}
              onChange={(e) => {
                if (e?.$d) setDateValue(item, e);
              }}
            />
          </LocalizationProvider>
        )}
      </>

      <>
        {['textarray'].includes(item.type) && (
          <>
            <Divider />
            <Stack direction="column" spacing={2}>
              <Typography variant="body1">{item.label}</Typography>
              {!_.isEmpty(values[item.id]) &&
                values[item.id]?.map((valueItem, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">{index + 1}.</Typography>
                    <TextField
                      type={item.type === 'password' ? 'password' : 'text'}
                      multiline="true"
                      required={Boolean(item.required)}
                      fullWidth
                      autoComplete={item.id}
                      label={item.label}
                      {...getFieldProps(item.id)}
                      error={Boolean(touched[item.id] && errors[item.id])}
                      helperText={touched[item.id] && errors[item.id]}
                      value={valueItem.value}
                      placeholder={item.placeholder}
                      onChange={(e) => setTextArrayValue(e, item, valueItem)}
                    />
                    <>
                      {isEligible('update_feedback') && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeTextArrayItem(item, valueItem)}
                            width={24}
                            height={24}
                            color={theme.palette.error.main}
                            icon={trash2Outline}
                          />
                          <Icon
                            style={{ cursor: 'pointer' }}
                            onClick={() => changeTextArrayPosition(item, index, 'up')}
                            width={24}
                            height={24}
                            color={index > 0 ? theme.palette.info.main : theme.palette.grey[500]}
                            icon={arrowCircleUpOutline}
                          />
                          <Icon
                            style={{ cursor: 'pointer' }}
                            onClick={() => changeTextArrayPosition(item, index, 'down')}
                            width={24}
                            height={24}
                            color={
                              index !== values[item.id].length - 1
                                ? theme.palette.info.main
                                : theme.palette.grey[500]
                            }
                            icon={arrowCircleDownOutline}
                          />
                        </Stack>
                      )}
                    </>
                  </Stack>
                ))}

              {isEligible('update_feedback') && (
                <Button onClick={() => addTextArrayItem(item)}>Tambah poin rekomendasi</Button>
              )}
            </Stack>
          </>
        )}
      </>

      <>
        {['radio'].includes(item.type) && (
          <>
            <Stack direction="column" spacing={2}>
              <FormControl error={Boolean(touched[item.id] && errors[item.id])}>
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 10 }}>
                    <FormLabel id={item.id}>{`${item.label}`}</FormLabel>
                  </Box>
                </Stack>
                <RadioGroup
                  sx={{ padding: 2 }}
                  aria-labelledby={item.label}
                  value={values[item.id]}
                  name={item.id}
                  onChange={(event) => {
                    setFieldValue(item.id, event.currentTarget.value);
                  }}
                >
                  {item?.options?.map((option, optionIndex) => (
                    <React.Fragment key={optionIndex}>
                      {option?.condition === 'layanan_status_must_be_approved' ? (
                        layananStatus === 'approved' && (
                          <FormControlLabel
                            value={option.id}
                            control={<Radio />}
                            label={option.label}
                            disabled={isSubmitting}
                          />
                        )
                      ) : (
                        <FormControlLabel
                          value={option.id}
                          control={<Radio />}
                          label={option.label}
                          disabled={isSubmitting}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </RadioGroup>
              </FormControl>
            </Stack>
          </>
        )}
      </>

      <>
        {['radio-with-textarea'].includes(item.type) && (
          <>
            <Stack direction="column" spacing={2}>
              <FormControl error={Boolean(touched[item.id] && errors[item.id])}>
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 10 }}>
                    <FormLabel id={item.id}>{`${item.label}`}</FormLabel>
                  </Box>
                </Stack>
                <RadioGroup
                  sx={{ padding: 2 }}
                  aria-labelledby={item.label}
                  value={values[item.id]}
                  name={item.id}
                  onChange={(event) => {
                    setFieldValue(item.id, event.currentTarget.value);
                  }}
                >
                  {item?.options?.map((option, optionIndex) => (
                    <FormControlLabel
                      key={optionIndex}
                      value={option.id}
                      control={<Radio />}
                      label={option.label}
                      disabled={isSubmitting}
                    />
                  ))}
                </RadioGroup>
                <Stack px={2} direction="row">
                  <Box sx={{ flex: 12 }}>
                    <TextField
                      value={dataDukungs[item.id]}
                      onChange={(e) =>
                        setDataDukungs({ ...dataDukungs, [item.id]: e.target.value })
                      }
                      label="Data dukung"
                      fullWidth
                    />
                  </Box>
                </Stack>
              </FormControl>
            </Stack>
          </>
        )}
      </>
    </>
  );
}
